import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

export type AgenticSectionId = "captions" | "broll" | "overlays" | "music";

interface AgenticAccordionContextValue {
  openId: AgenticSectionId | null;
  setOpenId: (id: AgenticSectionId | null) => void;
  expand: (id: AgenticSectionId) => void;
  toggle: (id: AgenticSectionId) => void;
}

const AgenticAccordionContext = createContext<AgenticAccordionContextValue | null>(null);

export function useAgenticAccordion(): AgenticAccordionContextValue {
  const ctx = useContext(AgenticAccordionContext);
  if (!ctx) {
    throw new Error(
      "useAgenticAccordion must be used inside <AgenticAccordion>",
    );
  }
  return ctx;
}

interface AgenticAccordionProps {
  defaultOpen?: AgenticSectionId | null;
  /** Caller-controlled expand intent. Bump this on every external expand request. */
  expandRequest?: { id: AgenticSectionId; nonce: number } | null;
  children: ReactNode;
  className?: string;
}

export function AgenticAccordion({
  defaultOpen = "captions",
  expandRequest = null,
  children,
  className,
}: AgenticAccordionProps) {
  const [openId, setOpenId] = useState<AgenticSectionId | null>(defaultOpen);
  const lastNonceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!expandRequest) return;
    if (lastNonceRef.current === expandRequest.nonce) return;
    lastNonceRef.current = expandRequest.nonce;
    setOpenId(expandRequest.id);
  }, [expandRequest]);

  const expand = useCallback((id: AgenticSectionId) => {
    setOpenId(id);
  }, []);

  const toggle = useCallback((id: AgenticSectionId) => {
    setOpenId((prev) => (prev === id ? null : id));
  }, []);

  const value = useMemo<AgenticAccordionContextValue>(
    () => ({ openId, setOpenId, expand, toggle }),
    [openId, expand, toggle],
  );

  return (
    <AgenticAccordionContext.Provider value={value}>
      <div className={cn("flex flex-col gap-2", className)}>{children}</div>
    </AgenticAccordionContext.Provider>
  );
}

interface SectionProps {
  id: AgenticSectionId;
  title: string;
  Icon: React.ComponentType<{ className?: string }>;
  /** One-line status shown next to the title (e.g. "3 cutaways applied"). */
  summary?: string | null;
  /** Optional accent (e.g. emerald dot when work has been applied). */
  accent?: "applied" | "active" | null;
  children: ReactNode;
  className?: string;
}

function Section({
  id,
  title,
  Icon,
  summary,
  accent = null,
  children,
  className,
}: SectionProps) {
  const { openId, toggle } = useAgenticAccordion();
  const open = openId === id;

  return (
    <section
      className={cn(
        "overflow-hidden rounded-2xl border border-border/50 bg-card/70 shadow-sm transition-colors",
        open && "bg-card",
        className,
      )}
      aria-label={title}
    >
      <button
        type="button"
        onClick={() => toggle(id)}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-muted/10",
          open && "bg-muted/5",
        )}
      >
        <span
          className={cn(
            "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/30",
            accent === "applied" && "bg-emerald-500/15 text-emerald-500 ring-emerald-500/30",
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-foreground">
            {title}
          </span>
          {summary && (
            <span className="truncate text-[11px] text-muted-foreground">
              {summary}
            </span>
          )}
        </span>
        {accent === "applied" && !open && (
          <span
            aria-hidden="true"
            className="inline-block h-2 w-2 shrink-0 rounded-full bg-emerald-500"
          />
        )}
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </section>
  );
}

AgenticAccordion.Section = Section;
